import { setContext } from '@apollo/client/link/context';

import type { GraphLoginAsync } from '../../../../../graphdebate/src/components/auth/LoadGraphLogin';
import { TOKEN_NAME } from '../../../constants/graphlogin';
import type { AccessToken } from '../../../types/auth';
import { isBrowser } from '../../../utils/fragments/isBrowser';

export type GetPublicKey = () => string;

export type GenerateAuthLinkArgs = {
  getToken?: GraphLoginAsync['getToken'];
  getPublicKey?: GetPublicKey;
  triggerRefreshToken?: () => Promise<void>;
  // Appartenance produit (B0_02b) : produit émetteur ('graphdebate'). Optionnel et paramétré par app
  // (graphdebate le passe ; graphlogin NON → son `me` ne marque pas, pas de pollution). Cf. ticket B0_02b.
  product?: string;
};

// const expiredJwt =
//   'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJfaWQiOiI2MzhjOWY4YjZlMTA2ZjRhN2NkZTAzZGMiLCJ1c2VybmFtZSI6ImFudG9pbmVvbCIsImVtYWlsIjoiYW50b2luZS5vcnlsYW1iYWxsZUBnbWFpbC5jb20iLCJmYWNlYm9va19pZCI6IjEwMTU5MzA5ODIwNTgwMjMzIiwiZ29vZ2xlX2lkIjoiMTA1NTczMjMyNzk0MzE3NDg2OTY1Iiwicm9sZSI6InVzZXIiLCJmcm9tX3dlYnNpdGUiOiI1ZDg4ZGEzZDNmNWY2MzJlMjI5N2Y0ZGMiLCJ2YWxpZGF0aW9uIjp7fSwibGFuZ3VhZ2UiOiJmciIsImlhdCI6MTcwMjU0OTcyOSwiZXhwIjoxNzA1MTQxNzI5fQ.-TIS6CxZei8wJVoR4bB-sn4U7XOdtmEokEu3rB4tFfo';

let prevToken: AccessToken | undefined = undefined;
let alreadyFailed = false;

export const generateAuthLink = ({ getToken, getPublicKey, product }: GenerateAuthLinkArgs) =>
  setContext(async (_op, { headers, gcInteraction }) => {
    let token = getToken ? await getToken() : null;

    // Fallback: if GraphLogin is not ready yet but the cookie is set (native flow), read it directly.
    if (!token && typeof document !== 'undefined') {
      const match = document.cookie.match(
        new RegExp(`(?:^|; )${TOKEN_NAME.replace(/([.*+?^${}()|[\\]\\\\])/g, '\\\\$1')}=([^;]*)`),
      );
      token = match?.[1] || null;
    }

    const publicKey = getPublicKey ? getPublicKey() : null;

    // Test code to simulate a 401 on a specific gql query.
    if (_op.operationName === 'HomeTopicsQuery') {
      let tokenChangedFromSecondReq = !!prevToken && token !== prevToken;
      prevToken = token;
      if (!tokenChangedFromSecondReq && !alreadyFailed /* Math.random() < 0.3 */) {
        // token = expiredJwt;
        alreadyFailed = true;
      }
    }

    return {
      headers: {
        ...headers,
        ...(publicKey ? { website: publicKey } : null),
        ...(token ? { Authorization: `JWT ${token}` } : null),
        ...(product ? { 'semio-product': product } : null),
        // Marqueur navigateur (B10_05), DEUX valeurs : `browser` = appel
        // navigateur ordinaire (montage de page compris) ; `browser-interaction`
        // = appel déclaré GESTE par son call-site via
        // `context: { gcInteraction: true }` (clic « charger la suite »,
        // re-tri, recherche saisie…). L'API ne facture une Interaction GraphQL
        // que sur `browser-interaction` — le nom d'opération ne sait pas
        // distinguer le montage du geste (option b, décision Mathias
        // 2026-08-31). ⚠ Ne JAMAIS poser `gcInteraction` sur un fetch de
        // montage : chaque appel marqué est FACTURÉ au client. Gate
        // `isBrowser`, PAS une option : le même client Apollo tourne dans
        // getStaticProps (SSR/ISR) — l'étendre au serveur recréerait
        // l'incident « 82 % de bots ». L'en-tête est déclaré dans le
        // Access-Control-Allow-Headers de l'API (routes.js) — l'y retirer
        // casserait le preflight de TOUS les appels.
        ...(isBrowser
          ? { 'x-gc-client': gcInteraction === true ? 'browser-interaction' : 'browser' }
          : null),
      },
    };
  });
